import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { useIsMobile } from "../hooks/useIsMobile";
import { C, font } from "../styles/tokens";
import { LEGAL_CONFIG } from "../config/legalConfig";

/**
 * www.tickandpass.com/account-deletion: how to delete an account and what
 * happens to it, for Google Play's account deletion requirement. Deleting
 * in the app keeps everything so the person can reactivate (decided 1 Oct
 * 2026); erasure requests follow section 18 of the Privacy Policy. Laid out
 * as the Terms and Privacy pages are.
 */
export function AccountDeletionPage() {
  const isMobile = useIsMobile();

  useEffect(() => {
    document.title = "TickAndPass - Delete your account";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      "How to delete your TickAndPass account, what is kept, and how to come back.";
  }, []);

  return (
    <PublicShell>
      <div
        style={{
          ...pageStyle,
          padding: isMobile ? "32px 16px 56px" : "56px 24px 80px",
        }}
      >
        <header style={headerStyle}>
          <h1 style={{ ...titleStyle, fontSize: isMobile ? 28 : 38 }}>
            Delete your TickAndPass account
          </h1>
          <p style={introStyle}>
            {LEGAL_CONFIG.companyName} runs TickAndPass. This page explains how
            to delete your account, what happens to your information, and how to
            come back.
          </p>
        </header>

        <div style={bodyStyle}>
          <section>
            <h2 style={sectionHeadingStyle}>1. Delete it in the app</h2>
            <ol style={stepsStyle}>
              <li>Open the TickAndPass app and sign in.</li>
              <li>Go to Settings, then Delete account.</li>
              <li>Enter your password, then confirm.</li>
            </ol>
            <p>
              A branch login is removed by its organisation's admin. If you are
              the only admin of an institution or organisation, make another
              officer an admin first.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>2. What happens</h2>
            <ul style={pointsStyle}>
              <li>You are signed out on every phone and browser at once.</li>
              <li>
                Nobody can sign in to the account, and TickAndPass stops sending
                it emails and notifications.
              </li>
              <li>
                Nothing is erased. Your credentials, verifications, payments and
                invoices are kept, and your shared profile link stops working.
              </li>
              <li>
                If you belong to a team, your decisions and messages stay with
                it, and its admins see your account as deleted. An admin can
                also disable it for good; it then cannot be reactivated.
              </li>
            </ul>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>3. Coming back</h2>
            <p>
              Sign in with the same email and password. We email a 6-digit code
              to the account's address; enter it within 10 minutes and the
              account is active again, as you left it.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              4. Erasing your personal information
            </h2>
            <p>
              Deleting in the app keeps your information so that you can come
              back. To ask us to erase your personal information instead, write
              to our privacy team from the address on your account, as section
              18 of our{" "}
              <Link to="/privacy-policy" style={linkStyle}>
                Privacy Policy
              </Link>{" "}
              describes. Records the law requires us to keep, such as invoices,
              are kept.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>5. Contact</h2>
            <div style={contactCardStyle}>
              <div>
                <strong>{LEGAL_CONFIG.companyName}</strong>
              </div>
              <div>
                Privacy team:{" "}
                <a
                  href={`mailto:${LEGAL_CONFIG.privacyEmail}`}
                  style={linkStyle}
                >
                  {LEGAL_CONFIG.privacyEmail}
                </a>
              </div>
              <div>
                Help with the app:{" "}
                <a
                  href={`mailto:${LEGAL_CONFIG.supportEmail}`}
                  style={linkStyle}
                >
                  {LEGAL_CONFIG.supportEmail}
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </PublicShell>
  );
}

const pageStyle = {
  maxWidth: 900,
  margin: "0 auto",
  fontFamily: font,
};

const headerStyle = {
  marginBottom: 36,
  borderBottom: `1px solid ${C.divider}`,
  paddingBottom: 24,
};

const titleStyle = {
  fontWeight: 800,
  color: C.navy,
  marginBottom: 12,
  letterSpacing: "-0.5px",
  lineHeight: 1.2,
};

const introStyle = {
  fontSize: 15,
  color: C.textMed,
  lineHeight: 1.7,
};

const bodyStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 32,
  fontSize: 15,
  color: C.textDark,
  lineHeight: 1.7,
};

const sectionHeadingStyle = {
  fontSize: 20,
  fontWeight: 700,
  color: C.navy,
  marginBottom: 10,
  letterSpacing: "-0.2px",
};

const stepsStyle = {
  listStyle: "decimal",
  paddingLeft: 22,
  marginBottom: 10,
};

const pointsStyle = {
  listStyle: "disc",
  paddingLeft: 22,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const linkStyle = {
  color: C.primary,
  textDecoration: "none",
  fontWeight: 600,
};

const contactCardStyle = {
  marginTop: 4,
  padding: 16,
  borderRadius: 8,
  background: C.bgMuted,
  border: `1px solid ${C.divider}`,
  fontSize: 14,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};
